		<!--
			The cell this renders into is sized by its content, so without a ceiling the longest
			sentence in here decides how wide the whole table is. A form is easier to read at this
			width anyway.
		-->
		<div class="w-full max-w-3xl">
			<div class="flex flex-wrap items-center justify-between gap-3">
				<div>
					@if (isNew()) {
						<span class="text-lg font-semibold" [translate]>New draft</span>
					} @else {
						<span class="font-mono text-lg font-semibold">{{ document().number }}</span>
					}
					<span class="badge badge-sm ml-2" [class.badge-warning]="document().status === 'draft'" [class.badge-success]="document().status === 'issued'">{{ document().status }}</span>
					<span class="text-base-content/60 ml-2 text-sm">{{ document().config.totals.totalTtc | currency: document().config.currency }}</span>
				</div>
				@if (document().status !== 'draft') {
					<div class="flex items-center gap-3">
						<span class="text-base-content/60 text-sm" [translate]>Issued and shared. It can no longer be changed.</span>
						@if (document().config.pdfFileId; as pdfFileId) {
							<lib-download-button [entityFileId]="pdfFileId" [downloadName]="document().number + '.pdf'"></lib-download-button>
						}
					</div>
				}
			</div>

			@if (document().status === 'draft') {
				<div class="mt-3 w-full overflow-x-auto">
					<table class="table table-sm">
						<thead>
							<tr>
								<th [translate]>Description</th>
								<th class="w-20 text-right" [translate]>Qty</th>
								<th class="w-32 text-right" [translate]>Unit price HT</th>
								<th class="w-24 text-right" [translate]>VAT %</th>
								<th class="w-12"></th>
							</tr>
						</thead>
						<tbody>
							@for (line of lines(); track $index) {
								<tr>
									<td>
										<input class="input input-bordered input-sm w-full" type="text" [ngModel]="line.label" (ngModelChange)="updateLine($index, 'label', $event)" [name]="'label' + $index" />
									</td>
									<td>
										<input class="input input-bordered input-sm w-full text-right" type="number" step="1" [ngModel]="line.quantity" (ngModelChange)="updateLine($index, 'quantity', +$event)" [name]="'qty' + $index" />
									</td>
									<td>
										<input class="input input-bordered input-sm w-full text-right" [class.input-error]="line.unitPriceHt < 0" type="number" step="0.01" [ngModel]="line.unitPriceHt" (ngModelChange)="updateLine($index, 'unitPriceHt', +$event)" [name]="'price' + $index" />
									</td>
									<td>
										<input class="input input-bordered input-sm w-full text-right" type="number" step="0.1" [ngModel]="line.vatRate" (ngModelChange)="updateLine($index, 'vatRate', +$event)" [name]="'vat' + $index" />
									</td>
									<td class="text-right">
										@if (lines().length > 1) {
											<button class="btn btn-ghost btn-xs" (click)="removeLine($index)">✕</button>
										}
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
				<div class="mt-1 flex flex-wrap items-center gap-4">
					<button class="btn btn-sm btn-ghost" (click)="addLine()"><span [translate]>Add a line</span></button>
					<!-- What these lines come to, before it is saved: the figure in the header is the
					     stored one, and while someone is typing the two are not the same document. -->
					<span class="text-base-content/60 text-sm">
						<span [translate]>Edited total TTC</span>
						<strong class="text-base-content ml-1">{{ totalTtc() | currency: document().config.currency }}</strong>
					</span>
				</div>
				@if (hasNegativeLine()) {
					<div role="alert" class="alert alert-error mt-3">
						<span [translate]>A line price cannot be negative (EN 16931 BR-27). A reduction is a lower price, never a negative line.</span>
					</div>
				}

				<!-- The same form the customer fills on their profile: one place to add a field to. -->
				<div class="mt-3">
					<lib-billing-details-form
						[party]="buyerParty()"
						(partyChange)="onBuyerChange($event)"
						[explanation]="buyerExplanation()"
						[baseCurrency]="document().config.currency"
						[indicativeCurrency]="indicativeCurrency()"
						(indicativeCurrencyChange)="indicativeCurrency.set($event)"
					></lib-billing-details-form>
				</div>
				@if (kind() === 'invoice') {
					<label class="form-control mt-3 block">
						<span class="label-text text-xs" [translate]>VAT exemption (required once a line is at 0 %)</span>
						<select
							class="select select-bordered select-sm w-full"
							data-testid="vat-exemption"
							[ngModel]="vatExemption()"
							(ngModelChange)="vatExemption.set($event)"
							name="vatExemption"
						>
							<option value="" [translate]>Choose the reason…</option>
							@for (exemption of vatExemptions(); track exemption.code) {
								<option [value]="exemption.mention">{{ exemption.label }} — {{ exemption.mention }}</option>
							}
							<!-- A mention chosen before this list changed still has to be visible. -->
							@if (vatExemption() && !vatExemptions().length) {
								<option [value]="vatExemption()">{{ vatExemption() }}</option>
							}
						</select>
					</label>
				}
				<!-- Below the editor, because every one of them acts on what is above. -->
				<div class="mt-4 flex flex-wrap items-center gap-2 border-t pt-3">
					<button class="btn btn-sm btn-outline" data-testid="save-draft" [disabled]="isBusy() || !hasChanges()" (click)="save()">
						@if (isNew()) {
							<span [translate]>Create draft</span>
						} @else {
							<span [translate]>Save on this draft</span>
						}
					</button>

						<button class="btn btn-sm btn-outline" [disabled]="isBusy()" (click)="preview()"><span [translate]>Preview PDF</span></button>
						<button class="btn btn-sm btn-outline" [disabled]="isChecking()" (click)="check()">
							@if (isChecking()) {
								<span class="loading loading-spinner loading-xs"></span>
							}
							<span [translate]>Check this file</span>
						</button>
						<!-- Off unless asked: our own rules cost nothing, the outside engine spends a credit
						     and sends the document to a third party. -->
						<label class="label cursor-pointer gap-2" [title]="'Sends the document to the outside validator. Costs a credit.'">
							<input
								type="checkbox"
								class="checkbox checkbox-sm"
								data-testid="use-external-validator"
								[checked]="useExternalValidator()"
								(change)="useExternalValidator.set($any($event.target).checked)"
							/>
							<span class="label-text text-xs" [translate]>ask the outside validator</span>
						</label>
						<button class="btn btn-sm btn-ghost text-error" [disabled]="isBusy() || isNew()" (click)="delete()"><span [translate]>Delete draft</span></button>
						<button class="btn btn-sm btn-primary" [disabled]="isBusy() || isRefused() || isNew()" [title]="isRefused() ? 'Fix what is listed below first.' : ''" (click)="issue()"><span [translate]>Issue and share</span></button>
				</div>
			}

			@let checked = report();
			@if (checked) {
				<div
					role="alert"
					class="alert mt-4 block w-full min-w-0 overflow-hidden break-words"
					[class.alert-success]="checked.valid"
					[class.alert-error]="!checked.valid"
				>
					<!-- Who answered decides the wording: a verdict from our own rules must never read as
					     one from the service, which is the whole reason the report names its target. -->
					<p class="font-semibold">
						@if (checked.targetExecuted === IN_PROCESS_TARGET) {
							@if (checked.valid) {
								<span [translate]>Our own rules accept this file.</span>
							} @else {
								<span [translate]>Our own rules refuse this file.</span>
							}
						} @else if (checked.valid) {
							<span [translate]>The outside validator accepted this file.</span>
						} @else {
							<span [translate]>The outside validator refused this file.</span>
						}
					</p>
					<p class="mt-1 text-xs">
						<span [translate]>Checked against</span>
						<span class="ml-1 font-mono">{{ checked.targetExecuted || '—' }}</span>
						@if (checked.profile) {
							<span class="ml-1">· {{ checked.profile }}</span>
						}
						<span class="ml-1">· {{ checked.errorCount }}</span>
						<span [translate]>errors</span>
						<span class="ml-1">· {{ checked.warningCount }}</span>
						<span [translate]>warnings</span>
						@if (checked.scope) {
							<span class="text-base-content/60 block">{{ checked.scope }}</span>
						}
					</p>
					<!--
						A verdict from a target that did not run is not a verdict. Both services warn
						about this themselves, and it is the one way a green tick here could mislead.
					-->
					@if (checked.targetExecuted !== checked.targetRequested || checked.targetStatus !== 'verified') {
						<p class="mt-1 text-xs font-semibold">
							<span [translate]>Careful: this is not the check that was asked for.</span>
							<span class="ml-1 font-mono break-all">{{ checked.targetRequested }} → {{ checked.targetExecuted || 'none' }} ({{ checked.targetStatus }})</span>
						</p>
					}
					@if (checked.profilesSkipped.length) {
						<p class="mt-1 text-xs font-semibold">
							<span [translate]>Rulesets that did not run:</span>
							<span class="ml-1 font-mono">{{ checked.profilesSkipped.join(', ') }}</span>
						</p>
					}
					<!--
						Sirene disagreeing about who the customer is counts as nothing in the summary counts, so
						it has to be shown on its own or a green tick hides it.
					-->
					@if (checked.registryChecks.length) {
						<div class="mt-2">
							<p class="text-xs font-semibold" [translate]>The public registry does not agree with this invoice:</p>
							<ul class="mt-1 list-outside list-disc pl-5 text-sm">
								@for (check of checked.registryChecks; track $index) {
									<li class="break-words whitespace-normal">
										@if (check.party) {
											<span class="badge badge-ghost badge-xs mr-1">{{ check.party }}</span>
										}
										<span>{{ check.message }}</span>
										@if (check.expected) {
											<span class="text-base-content/60 block text-xs">
												<span [translate]>Registry:</span> <span>{{ check.expected }}</span>
												<span> · </span>
												<span [translate]>Invoice:</span> <span>{{ check.declared }}</span>
											</span>
										}
									</li>
								}
							</ul>
						</div>
					}
					@if (checked.issues.length) {
						<ul class="mt-2 list-outside list-disc pl-5 text-sm">
							@for (issue of checked.issues; track $index) {
								<li class="break-words whitespace-normal">
									@if (issue.stage) {
										<span class="badge badge-ghost badge-xs mr-1">{{ issue.stage }}</span>
									}
									@if (issue.code) {
										<span class="font-mono">{{ issue.code }}</span>
										<span> — </span>
									}
									<span>{{ issue.message }}</span>
								</li>
							}
						</ul>
					}
				</div>
			}

			@if (complianceFailures().length) {
				<div role="alert" class="alert alert-error mt-4 block">
					<p class="font-semibold" [translate]>Not compliant yet, so it cannot be issued. Nothing has been numbered.</p>
					<ul class="mt-2 list-inside list-disc text-sm">
						@for (failure of complianceFailures(); track failure) {
							<li>{{ failure }}</li>
						}
					</ul>
				</div>
			}
		</div>